export const MAX_BACKGROUND_BYTES = 3 * 1024 * 1024;
export const MAX_STORED_BACKGROUNDS = 53;
export const ALLOWED_BACKGROUND_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

export const DB_NAME = 'gate-daily-dish-looks';
export const STORE_NAME = 'backgrounds';

export const validateBackgroundFile = (file: { type: string; size: number }): string | null => {
  if (!(ALLOWED_BACKGROUND_TYPES as readonly string[]).includes(file.type)) {
    return 'Chỉ nhận ảnh PNG, JPEG hoặc WebP.';
  }
  if (file.size > MAX_BACKGROUND_BYTES) {
    return 'Ảnh vượt quá 3MB.';
  }
  return null;
};

/**
 * ISO `YYYY-MM-DD` keys — trả về key cần xóa khi vượt `keep`.
 *
 * `protect` là key vừa được ghi: nó không bao giờ nằm trong danh sách xóa. Thiếu tham số này thì
 * việc tải ảnh cho một tuần cũ hơn mọi tuần đã lưu sẽ xóa đúng ảnh vừa ghi, vì sắp xếp tăng dần
 * đưa nó lên đầu danh sách ứng viên.
 */
export const pruneOldestKeys = (
  keys: string[],
  keep = MAX_STORED_BACKGROUNDS,
  protect?: string
): string[] => {
  const excess = keys.length - keep;
  if (excess <= 0) {
    return [];
  }
  const candidates = [...keys].sort().filter((key) => key !== protect);
  return candidates.slice(0, excess);
};

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const withStore = async <T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const request = run(tx.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      // Firefox/Safari abort transaction khi hết quota hoặc bị evict mà không phải lúc nào cũng
      // bắn request.onerror. Thiếu nhánh này thì Promise treo vĩnh viễn: nút Tải ảnh nền im lặng,
      // và tệ hơn là hydrate kẹt trước khi bỏ cờ skipPersistRef nên tuần đó không lưu được nữa.
      tx.onabort = () => reject(tx.error ?? new DOMException('Transaction aborted', 'AbortError'));
    });
  } finally {
    db.close();
  }
};

export const listBackgroundKeys = async (): Promise<string[]> => {
  const keys = await withStore('readonly', (store) => store.getAllKeys());
  return (keys as IDBValidKey[]).map(String);
};

export const getLocalBackground = async (dateFrom: string): Promise<Blob | null> => {
  const value = await withStore('readonly', (store) => store.get(dateFrom));
  return value instanceof Blob ? value : null;
};

export const deleteLocalBackground = async (dateFrom: string): Promise<void> => {
  await withStore('readwrite', (store) => store.delete(dateFrom));
};

export const putLocalBackground = async (dateFrom: string, blob: Blob): Promise<string[]> => {
  await withStore('readwrite', (store) => store.put(blob, dateFrom));
  const keys = await listBackgroundKeys();
  const dropped = pruneOldestKeys(keys, MAX_STORED_BACKGROUNDS, dateFrom);
  await Promise.all(dropped.map((key) => deleteLocalBackground(key)));
  return dropped;
};

export const resizeBackgroundFile = async (file: File): Promise<Blob> => {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1920 / bitmap.width, 1080 / bitmap.height);
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return file;
  }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Không xử lý được ảnh.'));
        }
      },
      mime,
      0.86
    );
  });
};
